// ============================================================
// SuggestionsHistorique — CE QUE L'HISTORIQUE DIT, à côté de ce que
// l'agent a lu.
// ------------------------------------------------------------
// C'est la couche 2 de l'apprentissage rendue VISIBLE, et c'est ce qui
// distingue une promesse d'une affirmation.
//
//     l'agent lit       → « material_label: C43 »
//     l'historique dit  → « lu C43 3 fois, retenu C45 les 3 fois »
//     l'écran montre LES DEUX, le deviseur tranche en un clic.
//
// ── Pourquoi ce fichier existe — 7 septembre 2026 ──
//
// Le serveur calculait ces suggestions à CHAQUE lecture de plan, les
// renvoyait, et personne ne les lisait : leur seul consommateur,
// `PropositionPanel.jsx`, n'était plus importé nulle part depuis que
// les pièces naissent du dépôt automatique. Les couches 1 et 2 étaient
// calculées, payées et jetées.
//
// ── DEUX RÈGLES QUI NE SE NÉGOCIENT PAS ──
//
// **Rien ne s'applique tout seul.** La suggestion s'affiche, elle ne
// remplace pas. C'est l'invariant du projet : l'IA propose, l'homme
// décide. Un remplacement silencieux ferait monter un indicateur
// d'apprentissage sans qu'aucun apprentissage ait eu lieu (§11, 11e).
//
// **Une suggestion déjà appliquée disparaît.** Le serveur compare la
// suggestion à ce que l'agent avait LU ; l'écran, lui, la compare à ce
// que la pièce porte MAINTENANT. Sans ça, le deviseur qui vient de
// corriger verrait le programme lui redemander la même chose.
// ============================================================

import { useEffect, useState } from 'react';
import Badge from '../../ui/Badge.jsx';
import Button from '../../ui/Button.jsx';
import { api } from '../../lib/api.js';

/** Les champs qu'on sait présenter, et leur nom à l'écran. */
const LIBELLES = {
  ref: 'Référence',
  plan_index: 'Indice',
  name: 'Désignation',
  qty: 'Quantité',
  material_label: 'Matière',
  is_purchase: 'Composant du commerce',
  density: 'Densité',
  price_per_kg: 'Prix au kilo',
};

const montrer = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  return String(v);
};

const memeValeur = (a, b) => montrer(a).trim().toLowerCase() === montrer(b).trim().toLowerCase();

export function SuggestionsHistorique({ devisId, piece, disabled, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const pieceId = piece?.id ?? null;

  useEffect(() => {
    let annule = false;
    if (!devisId || !pieceId) { setSuggestions([]); return undefined; }
    api.get(`/devis/${devisId}/pieces/${pieceId}/suggestions`)
      // Une suggestion indisponible n'est pas une panne d'écran : c'est
      // le cas normal sur une pièce sans historique. On se tait.
      .then((r) => { if (!annule) setSuggestions(r?.suggestions ?? []); })
      .catch(() => { if (!annule) setSuggestions([]); });
    return () => { annule = true; };
  }, [devisId, pieceId]);

  // Ce que la pièce porte DÉJÀ n'a plus à être suggéré.
  const restantes = suggestions.filter(
    (s) => LIBELLES[s.field] && !memeValeur(piece?.[s.field], s.suggested),
  );
  if (!restantes.length) return null;

  return (
    <div className="suggestions">
      <p className="caps">
        Ce que l’historique dit
        <Badge tone="info">{restantes.length}</Badge>
      </p>
      <p className="field__hint">
        Des pièces comparables ont déjà été corrigées. Rien n’est appliqué :
        c’est vous qui tranchez.
      </p>

      <ul className="suggestions__liste">
        {restantes.map((s) => (
          <li key={s.field} className="suggestions__item">
            <p className="suggestions__champ">{LIBELLES[s.field]}</p>
            <p className="suggestions__valeurs">
              <span className="suggestions__lu">{montrer(piece?.[s.field])}</span>
              <span aria-hidden="true"> → </span>
              <strong>{montrer(s.suggested)}</strong>
              {s.strength === 'strong' ? (
                <Badge tone="ok">signal net</Badge>
              ) : (
                <Badge tone="warn">tendance</Badge>
              )}
            </p>
            {/* La RAISON, en toutes lettres. Une suggestion sans sa
                raison est un ordre ; avec sa raison, c'est un argument
                — et c'est ce qui la rend contestable, donc utile. */}
            <p className="field__hint">{s.reason}</p>
            <Button
              size="sm"
              variant="secondary"
              disabled={disabled}
              onClick={() => onChange({ [s.field]: s.suggested })}
            >
              Retenir « {montrer(s.suggested)} »
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SuggestionsHistorique;
